// AddressConfigCrud.jsx
import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../pages.css';
import {
  MButton,
  LoadingContext,
  M_StateActions,
  MHeader,
  NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';

//==============================================================================================================================================================
// Helpers
//==============================================================================================================================================================

//==============================================================================================================================================================
// Converts a string to camelCase (payload keys built from Headers).
//==============================================================================================================================================================
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

//==============================================================================================================================================================
// Returns true if the option represents a key field.
//==============================================================================================================================================================
const isKeyField = (opt) => !!opt.IsKey;

//==============================================================================================================================================================
// Returns true if the field is an audit field (never rendered in forms).
//==============================================================================================================================================================
const isAudit = (name) =>
  name === 'CreatedAt' || name === 'UpdatedAt' || name === 'CreatedBy' || name === 'UpdatedBy';

//==============================================================================================================================================================
// includeInCreate_ApiKey
// Override for CREATE mode on ApiKeys pages.
// Ensures that the "ApiKey" field appears on Create even if marked
// IsHiddenEdit by backend metadata. All other rules follow defaults:
// - exclude keys and audit
// - exclude Inactive
// - honor IsHiddenEdit for other fields
//==============================================================================================================================================================
const includeInCreate_ApiKey = (opt) => {
  if (!opt) return false;
  if (isKeyField(opt) || isAudit(opt.Name)) return false;
  if (opt.Name === 'Inactive') return false;
  // Special case: allow ApiKey even if IsHiddenEdit
  if (opt.Name === 'ApiKey') return true;
  // Default behavior for the rest
  if (opt.IsHiddenEdit) return false;
  return true;
};

//==============================================================================================================================================================
// --------------- COMPONENT -----------------------
//==============================================================================================================================================================
export default function ApiKeyConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  const selectedTab = M_State.find((o) => o.key === 'selectedTab')?.value;

  const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
  const globalLine = M_State.find((obj) => obj.key === 'ApiKeyLine')?.value;

  const [id, setId] = useState(M_State.find((obj) => obj.key === 'selectedTab')?.value);
  const [operation, setOperation] = useState(globalOperation);
  const [sessionLine, setSessionLine] = useState(globalLine);

  const isViewMode = operation?.toLowerCase() === 'view';

  // entity and options
  const [apiKeyInfo, setApiKeyInfo] = useState({});
  const [allOptions, setAllOptions] = useState([]); // Raw Options, DynamicForm will filter/sort
  const [isFirst, setIsFirst] = useState(true);
  const [apiKeyId, setApiKeyId] = useState(sessionLine?.Id);

  // imperative access to DynamicForm
  const formRef = useRef(null);

  // services
  const svcApiKeys = useMemo(() => new BufferService('ApiKeys', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('ApiKeys/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('ApiKeys/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  // NEW: refs to avoid stale closures inside async callbacks
  const idRef = useRef(id);
  const apiKeyIdRef = useRef(apiKeyId);
  const operationRef = useRef(operation);
  useEffect(() => { idRef.current = id; }, [id]);
  useEffect(() => { apiKeyIdRef.current = apiKeyId; }, [apiKeyId]);
  useEffect(() => { operationRef.current = operation; }, [operation]);

  // NEW: per-tab token to drop late async state updates after tab switch
  const tabTokenRef = useRef(0);
  useEffect(() => { tabTokenRef.current += 1; }, [id]);

  //================================================================================================================================================================
  // Rename Tab
  //================================================================================================================================================================

  //================================================================================================================================================================
  // Updates the tab label when language/operation/display name changes.
  // Uses sessionLine.Name first, then apiKeyInfo.Name, then base label.
  //================================================================================================================================================================
  useEffect(() => {
    if (!selectedTab || !operation) return;

    const key =
      operation === 'Create' ? 'LabelNewApiKey' : operation === 'Edit' ? 'LabelEditApiKey' : 'LabelViewApiKey';
    const name = sessionLine?.Name ?? apiKeyInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);

  }, [t, selectedTab, operation, sessionLine?.Name, apiKeyInfo?.Name]);

  //================================================================================================================================================================
  // Boot: restore snapshot and decide fetch
  //================================================================================================================================================================

  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const snapCI = sessionForThisTab.find((x) => x.key === 'apiKeyInfo')?.value;
    const snapIF = sessionForThisTab.find((x) => x.key === 'isFirst')?.value;
    const snapOP = sessionForThisTab.find((x) => x.key === 'operation')?.value;
    const snapSL = sessionForThisTab.find((x) => x.key === 'sessionLine')?.value;
    const snapOptions = sessionForThisTab.find((x) => x.key === 'allOptions')?.value
      ?? sessionForThisTab.find((x) => x.key === 'formSchema')?.value; // backward compatibility

    if (snapCI) setApiKeyInfo(snapCI);
    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) {
      setSessionLine(seedLine);
      if (seedLine.Id) setApiKeyId(seedLine.Id);
    }
    if (Array.isArray(snapOptions)) setAllOptions(snapOptions);

    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'ApiKeyLine' }));

    fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(() => {
      if (seedOperation === 'Create') {
        ctx.setIsLoad(false);
      } else if (seedOperation && seedLine?.Id) {
        // fetchDetails handles loader itself
        fetchDetails(seedLine?.Id);
      } else {
        ctx.setIsLoad(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //================================================================================================================================================================
  // Per-tab persistence
  //================================================================================================================================================================
  // NEW: debounced snapshot to reduce churn and avoid rapid re-renders
  const saveSnapshotTimer = useRef(null);
  useEffect(() => {
    if (!id) return;

    // Merge current RHF values to persist unsaved edits across tabs
    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view' ? apiKeyInfo : { ...apiKeyInfo, ...currentFormValues };

    const sessionData = [
      { key: 'apiKeyInfo', value: infoToPersist },
      { key: 'isFirst', value: isFirst },
      { key: 'operation', value: operation },
      { key: 'sessionLine', value: sessionLine },
      { key: 'allOptions', value: allOptions },
      // NEW: also store current entity id to avoid leaking across tabs
      { key: 'apiKeyId', value: apiKeyId }
    ];

    if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    saveSnapshotTimer.current = setTimeout(() => {
      dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, 80);

    return () => {
      if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    };
  }, [id, apiKeyInfo, isFirst, operation, sessionLine, allOptions, dispatch, apiKeyId]);

  //================================================================================================================================================================
  // Respond to active-tab change
  //================================================================================================================================================================
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  //================================================================================================================================================================
  // getSessionStorageData
  // Restores tab snapshot if available; otherwise refetches options and,
  // for Edit/View, the entity details.
  //================================================================================================================================================================
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);
    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;
      const tempCI = pick('apiKeyInfo');
      const tempIF = pick('isFirst');
      const tempOP = pick('operation');
      const tempSL = pick('sessionLine');
      const tempOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility

      setApiKeyInfo(tempCI || {});
      setIsFirst(tempIF ?? false);
      setOperation(tempOP);
      setSessionLine(tempSL);
      if (tempSL?.Id) setApiKeyId(tempSL.Id);
      const savedId = pick('apiKeyId');
      if (!tempSL?.Id && savedId) setApiKeyId(savedId);
      if (Array.isArray(tempOptions)) setAllOptions(tempOptions);
      ctx.setIsLoad(false);
    } else {
      fetchOptions(operation, sessionLine, { skipDetails: true }).finally(() => {
        if (operation !== 'Create' && sessionLine?.Id) {
          fetchDetails(sessionLine?.Id);
        } else {
          ctx.setIsLoad(false);
        }
      });
    }
  };

  //================================================================================================================================================================
  // Language changes (i18n) - Updates tab title and refetches options to update field labels.
  //================================================================================================================================================================
  useEffect(() => {
    if (!operation  || isFirst) return; // skip first render or no operation set
    fetchOptions(operation, sessionLine, { skipDetails: true });

    const key =
      operation === 'Create' ? 'LabelNewApiKey' : operation === 'Edit' ? 'LabelEditApiKey' : 'LabelViewApiKey';
    const name = sessionLine?.Name ?? apiKeyInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  //================================================================================================================================================================
  // fetchOptions
  // Fetches raw Options for ApiKeys.
  // Steps:
  // 1) Call /Options and store raw options (DynamicForm will filter per mode).
  // 2) Optionally fetch details for Edit/View.
  //================================================================================================================================================================
  const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcApiKeys.options();
      if (isFailurePayload(res) || res?.name === 'AxiosError') {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        ctx.setIsLoad(false);
        return;
      }
      if (myToken !== tabTokenRef.current) return;
      setAllOptions(res || []);

      const mode = op || operationRef.current || 'View';
      if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
  };

  //================================================================================================================================================================
  // Fetch details (Edit/View)
  //================================================================================================================================================================
  const fetchDetails = async (idToGet) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcApiKeys.getId(idToGet);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      if (myToken !== tabTokenRef.current) return;
      setApiKeyInfo(res || {});
      setApiKeyId(res?.Id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
  };

  //================================================================================================================================================================
  // buildPayloadFromValues
  // Builds DTO payload from values emitted by DynamicForm and raw options.
  // Steps:
  // 1) Iterate over options and pick the correct source key per field.
  // 2) Coerce booleans and keep scalars as-is.
  // 3) Lower camel case keys for DTO and assemble the object.
  //================================================================================================================================================================
  const buildPayloadFromValues = (values) => {
    const options = Array.isArray(allOptions) ? allOptions : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      const keySource = opt.Name; // no FKs on ApiKeys today, Name is enough
      const payloadKey = toCamel(opt.Header || keySource);

      let v;
      if (opt.Type === 'boolean' || opt.Type === 'bool') {
        v = !!values?.[keySource];
      } else {
        v = values?.[keySource];
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  //================================================================================================================================================================
  // onSubmitDynamic
  // Receives values from DynamicForm and performs create/update flow.
  // Steps:
  // 1) Build DTO payload.
  // 2) Call create or update service.
  // 3) Toast result and close the tab.
  //================================================================================================================================================================
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);
        const op = (operationRef.current || '').toLowerCase();
        const currentId = apiKeyIdRef.current;
        const res = op === 'create'
          ? await svcCreate.post(payload)
          : await svcUpdate.update(currentId, payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        toastMessages('success', '', op === 'create' ? t('ApiKeyCreated') : t('ApiKeyUpdated'));
        handleTabClose(idRef.current);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [buildPayloadFromValues, ctx, handleTabClose, svcCreate, svcUpdate, t, toastMessages]
  );

  //================================================================================================================================================================
  // triggerFormSubmit
  // Triggers DynamicForm submit programmatically via imperative ref.
  //================================================================================================================================================================
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  //================================================================================================================================================================
  // onRefresh
  // Re-fetches options and details if applicable for current mode.
  //================================================================================================================================================================
  const onRefresh = () => {
    fetchOptions(operation, sessionLine);
  };

  //================================================================================================================================================================
  // handleFormValuesChange
  // Receives small patches from DynamicForm and persists them in the
  // per-tab snapshot so edits survive tab switches.
  //================================================================================================================================================================
  const handleFormValuesChange = useCallback(
    (patch, meta) => {
      if (!patch || !id) return;

      const prevTabState = M_State.find((o) => o.key === id)?.value || [];
      const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
      const merged = { ...(pick('apiKeyInfo') || apiKeyInfo || {}), ...patch };

      const updated = [
        ...prevTabState.filter((x) => x.key !== 'apiKeyInfo'),
        { key: 'apiKeyInfo', value: merged }
      ];
      dispatch(M_StateActions.addM_State({ key: id, value: updated }));
    },
    [M_State, id, apiKeyInfo, dispatch]
  );

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t('RefreshTable')}
            />
            {!isViewMode && (
              <MButton
                tooltip={t('Save')}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t('Save')}
              />
            )}
          </>
        }
      />

      <div style={{
            padding: '20px',
            background: 'var(--color-white)',
          }}>
        <div className="firstDivContainer">
          <DynamicForm
            key={`df-apikey-${id}-${operation}-${apiKeyId ?? 'new'}`}
            formRef={formRef}
            mode={(operation || 'View').toLowerCase()}
            schema={allOptions}
            data={apiKeyInfo}
            onSubmit={onSubmitDynamic}
            // No FKs currently; use defaults of DynamicForm for view/edit filtering.
            includeInCreate={includeInCreate_ApiKey}
            onValuesChange={handleFormValuesChange}
            title={t('ApiKey Information')}
            containerClassName="alignInputsUserForm"
          />
        </div>
      </div>
    </>
  );
}

ApiKeyConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
